import React from 'react';
import { Box, Typography } from '@mui/material';
import type { Timeline } from './TenderQPage';

interface TimelineEvent {
  date: Date;
  label: string;
  isToday: boolean;
}

interface TimelineComponentProps {
  timeline: Timeline;
}

function buildEvents(record: Timeline): TimelineEvent[] {
  const events: TimelineEvent[] = [];
  const maxReasonableYear = 2100;

  const add = (dateStr: string | undefined, label: string) => {
    if (dateStr) {
      const date = new Date(dateStr);
      if (!isNaN(date.getTime()) && date.getFullYear() < maxReasonableYear) {
        events.push({ date, label, isToday: false });
      }
    }
  };

  add(record.docs_available_from, 'Docs Available');
  add(record.question_round_1, 'Q&A Round 1');
  add(record.question_round_2, 'Q&A Round 2');
  add(record.question_round_3, 'Q&A Round 3');
  add(record.docs_available_to, 'Docs Close');
  // tender_start is the simap offer deadline (last day to submit a proposal);
  // tender_end is how long submitted offers must remain valid (Bindefrist).
  add(record.tender_start, 'Submission Deadline');
  add(record.tender_end, 'Offer Validity Ends');

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const maxDate = events.length > 0
    ? events.reduce((max, e) => (e.date > max ? e.date : max), new Date(0))
    : null;

  if (maxDate && today <= maxDate) {
    events.push({ date: today, label: 'Today', isToday: true });
  }

  events.sort((a, b) => a.date.getTime() - b.date.getTime());
  return events;
}

// Exported for tests. A concluded project (SIMAP abandonment / award) has no
// upcoming step: the countdown would invite work on a closed tender.
export function getNextEventMessage(
  events: TimelineEvent[],
  concluded?: Timeline['concluded'],
  now: Date = new Date(),
): string | null {
  if (concluded === 'abandoned') return 'Abandoned — no submission possible';
  if (concluded === 'awarded') return 'Awarded — closed';
  if (events.length === 0) return null;
  const today = new Date(now);
  today.setHours(0, 0, 0, 0);
  const upcoming = events.find(e => !e.isToday && e.date >= today);
  if (!upcoming) return 'Closed';
  const diffDays = Math.ceil((upcoming.date.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return `${upcoming.label} is today`;
  if (diffDays === 1) return `1 day until ${upcoming.label}`;
  return `${diffDays} days until ${upcoming.label}`;
}

const formatDate = (date: Date) =>
  date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

const TimelineComponent: React.FC<TimelineComponentProps> = ({ timeline }) => {
  const events = buildEvents(timeline);
  if (events.length === 0) return null;

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const concluded = Boolean(timeline.concluded);
  const nextMsg = getNextEventMessage(events, timeline.concluded);

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 0.5 }}>
        <Typography variant="body2" sx={{ fontWeight: 500, fontSize: 'inherit' }}>
          Timeline
        </Typography>
        {nextMsg && (
          <Typography variant="caption" sx={{
            color: timeline.concluded === 'abandoned' ? 'error.main'
              : (concluded || nextMsg === 'Closed') ? 'text.disabled' : 'primary.main',
            fontSize: '0.65rem', fontWeight: 500 }}>
            {nextMsg}
          </Typography>
        )}
      </Box>

      {events.map((event, i) => {
        // Every remaining step is moot once the project is concluded.
        const isPast = concluded || (event.date < today && !event.isToday);
        const { isToday } = event;
        const isLast = i === events.length - 1;
        const dotSize = isToday ? 10 : 7;

        return (
          <Box key={i} sx={{ display: 'flex', opacity: isPast ? 0.45 : 1 }}>
            {/* Label — right-aligned; wide enough for "Submission Deadline" on one line */}
            <Typography variant="body2" sx={{
              width: 120,
              textAlign: 'right',
              flexShrink: 0,
              pr: 0.75,
              fontSize: '0.7rem',
              fontWeight: isToday ? 700 : 500,
              color: isToday ? 'primary.main' : 'text.primary',
              lineHeight: '16px',
              whiteSpace: 'nowrap',
            }}>
              {event.label}
            </Typography>

            {/* Dot + connector line */}
            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              width: 14,
              flexShrink: 0,
            }}>
              <Box sx={{
                width: dotSize,
                height: dotSize,
                borderRadius: '50%',
                bgcolor: isToday ? 'primary.main' : isPast ? 'grey.400' : 'grey.600',
                flexShrink: 0,
                mt: '4px',
                boxShadow: isToday ? '0 0 0 3px rgba(25,118,210,0.15)' : 'none',
              }} />
              {!isLast && (
                <Box sx={{
                  width: 1.5,
                  flexGrow: 1,
                  minHeight: 4,
                  bgcolor: 'grey.300',
                }} />
              )}
            </Box>

            {/* Date — left-aligned */}
            <Typography variant="caption" sx={{
              pl: 0.75,
              fontSize: '0.68rem',
              color: isToday ? 'primary.main' : 'text.secondary',
              fontWeight: isToday ? 600 : 400,
              whiteSpace: 'nowrap',
              lineHeight: '16px',
              pb: isLast ? 0 : 0.25,
            }}>
              {formatDate(event.date)}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
};

export default TimelineComponent;
